import { sortBy, keyBy } from 'lodash-es';
import * as C from '../constants';
import router from '../../../router';
import CoreService from '../../../services/core-service';
import ReminderService from '../../../services/reminder-service';
import Snackbar from '../../../services/snackbar-service';

export default {
  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.REMINDERS_RESET);
  },

  /**
   * @param commit
   * @param dispatch
   * @param domain
   */
  setTemplateDomain({ commit, dispatch }, domain) {
    commit(C.REMINDERS_TEMPLATES_DOMAIN_SET, domain);
    dispatch('getTemplates', domain.id);
  },

  /**
   * @param commit
   */
  loadDomains({ commit }) {
    commit(C.REMINDERS_DOMAINS_PENDING);
    return CoreService.getDomains().then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_DOMAINS_NO_CONTENT);
      } else {
        commit(C.REMINDERS_DOMAINS_SUCCESS, sortBy(res.data, ['brandId', 'name']));
      }
    }).catch(() => {
      commit(C.REMINDERS_DOMAINS_FAILURE);
    });
  },

  /**
   * @param commit
   * @param getters
   * @param domainId
   */
  getTemplates({ commit, getters }, domainId) {
    commit(C.REMINDERS_TEMPLATES_PENDING);
    ReminderService.getTemplates(domainId).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_TEMPLATES_NO_CONTENT);
      } else {
        const options = keyBy(getters.getAttachmentOptions, 'id');
        res.data.forEach((template) => {
          const attachments = [];
          template.attachments.forEach((attachment) => {
            attachments.push({ ...options[attachment.type] });
          });
          template.attachments = attachments;
        });
        commit(C.REMINDERS_TEMPLATES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_TEMPLATES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param template
   */
  createTemplate({ commit }, template) {
    commit(C.REMINDERS_TEMPLATE_SAVE_PENDING);
    return ReminderService.createTemplate(template).then(() => {
      commit(C.REMINDERS_TEMPLATE_SAVE_SUCCESS);
    }).catch(() => {
      commit(C.REMINDERS_TEMPLATE_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   * @param template
   */
  editTemplate({ commit }, template) {
    commit(C.REMINDERS_TEMPLATE_SAVE_PENDING);
    return ReminderService.editTemplate(template).then(() => {
      commit(C.REMINDERS_TEMPLATE_SAVE_SUCCESS);
    }).catch(() => {
      commit(C.REMINDERS_TEMPLATE_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param domain
   */
  setRuleDomain({ commit, dispatch }, domain) {
    commit(C.REMINDERS_RULES_DOMAIN_SET, domain);
    dispatch('getRules');
    dispatch('getTemplatesForRules');
    dispatch('getRevisions');
    dispatch('getSettings');
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  getRules({ commit, state, dispatch }) {
    if (!state.rules.domain) {
      return;
    }
    commit(C.REMINDERS_RULES_PENDING);
    ReminderService.getRulesForDomain(
      state.rules.domain.id,
      state.rules.currentRevision,
    ).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_RULES_NO_CONTENT);
      } else {
        commit(C.REMINDERS_RULES_SUCCESS, res.data);
        if (!state.rules.currentRevision) {
          dispatch('setRevision', res.data.revision);
        }
      }
    }).catch(() => {
      commit(C.REMINDERS_RULES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   */
  getSettings({ commit, state }) {
    commit(C.REMINDERS_RULES_SETTINGS_PENDING);
    ReminderService.getSettings(
      state.rules.domain.id,
    ).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_RULES_SETTINGS_NO_CONTENT);
      } else {
        res.data.forEach((item) => {
          if (typeof item.data === 'string') {
            try {
              const result = JSON.parse(item.data);
              const type = Object.prototype.toString.call(result);
              if (type === '[object Object]' || type === '[object Array]') {
                item.data = result;
              }
            } catch (err) {
              item.data = 'invalid';
            }
          } else {
            item.data = 'invalid';
          }
        });
        commit(C.REMINDERS_RULES_SETTINGS_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  createSetting({ commit, state, dispatch }, setting) {
    commit(C.REMINDERS_RULES_SETTINGS_ACTION_PENDING);
    return ReminderService.createSetting(state.rules.domain.id, setting).then(() => {
      commit(C.REMINDERS_RULES_SETTINGS_ACTION_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The new exception has been created');
      return Promise.resolve();
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_ACTION_FAILURE);
      Snackbar.error(dispatch, 'Unable to save the new exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  updateSetting({ commit, state, dispatch }, setting) {
    commit(C.REMINDERS_RULES_SETTINGS_ACTION_PENDING);
    return ReminderService.updateSetting(state.rules.domain.id, setting).then(() => {
      commit(C.REMINDERS_RULES_SETTINGS_ACTION_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been updated');
      return Promise.resolve();
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_ACTION_FAILURE);
      Snackbar.error(dispatch, 'Unable to update the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  deleteSetting({ commit, state, dispatch }, setting) {
    commit(C.REMINDERS_RULES_SETTINGS_DELETE_PENDING);
    return ReminderService.deleteSetting(state.rules.domain.id, setting).then(() => {
      commit(C.REMINDERS_RULES_SETTINGS_DELETE_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been deleted');
      return Promise.resolve();
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_DELETE_FAILURE);
      Snackbar.error(dispatch, 'Unable to delete the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param setting
   */
  activateSetting({ commit, state, dispatch }, setting) {
    commit(C.REMINDERS_RULES_SETTINGS_DELETE_PENDING);
    return ReminderService.activateSetting(state.rules.domain.id, setting).then(() => {
      commit(C.REMINDERS_RULES_SETTINGS_DELETE_SUCCESS);
      dispatch('getSettings');
      Snackbar.success(dispatch, 'The exception has been activated');
      return Promise.resolve();
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_DELETE_FAILURE);
      Snackbar.error(dispatch, 'Unable to restore the exception');
      return Promise.reject();
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param settingId
   */
  getLogs({ commit, state, dispatch }, settingId) {
    commit(C.REMINDERS_RULES_SETTINGS_LOGS_PENDING);
    ReminderService.getLogs(state.rules.domain.id, settingId).then((res) => {
      commit(C.REMINDERS_RULES_SETTINGS_LOGS_SUCCESS, res.data);
    }).catch(() => {
      commit(C.REMINDERS_RULES_SETTINGS_LOGS_FAILURE);
      Snackbar.error(dispatch, 'Unable to fetch the logs for this exception');
    });
  },

  /**
   * @param commit
   * @param domainId
   */
  getTemplatesForRules({ commit, state }) {
    commit(C.REMINDERS_RULES_TEMPLATES_PENDING);
    return ReminderService.getTemplates(state.rules.domain.id).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_RULES_TEMPLATES_NO_CONTENT);
      } else {
        commit(C.REMINDERS_RULES_TEMPLATES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_RULES_TEMPLATES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param template
   */
  addTemplate({ commit, state }, template) {
    template.order = state.rules.rules.item.templates.length + 1;
    commit(C.REMINDERS_RULES_TEMPLATES_ADD, template);
  },

  /**
   * @param commit
   * @param from
   * @param templateIds
   */
  swapTemplate({ commit, state }, templateIds) {
    const from = state.rules.rules.item.templates.findIndex(
      rule => rule.id === templateIds.from,
    );
    const to = state.rules.rules.item.templates.findIndex(
      rule => rule.id === templateIds.to,
    );
    commit(C.REMINDERS_RULES_TEMPLATES_SWAP_ORDER, { from, to });
  },

  /**
   * @param commit
   * @param state
   * @param templateId
   */
  deleteTemplate({ commit, state }, templateId) {
    const index = state.rules.rules.item.templates.findIndex(rule => rule.id === templateId);
    commit(C.REMINDERS_RULES_TEMPLATES_DELETE, {
      index,
      order: state.rules.rules.item.templates[index].order,
    });
  },

  /**
   * @param commit
   * @param state
   * @param valid
   * @param template
   */
  updateRuleTemplate({ commit, state }, template) {
    const index = state.rules.rules.item.templates.findIndex(t => t.id === template.id);
    commit(C.REMINDERS_RULES_TEMPLATES_UPDATE, { index, template });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  createRevision({ commit, state, dispatch }) {
    commit(C.REMINDERS_RULES_SAVE_PENDING);
    ReminderService.createRevision(
      state.rules.domain.id,
      state.rules.rules.item.templates,
    ).then(() => {
      dispatch('setRevision', state.rules.rules.item.revision + 1);
      commit(C.REMINDERS_RULES_SAVE_SUCCESS);
      dispatch('getRevisions');
    }).catch(() => {
      commit(C.REMINDERS_RULES_SAVE_FAILURE);
    });
  },

  /**
   * @param commit
   */
  resetRevision({ commit }) {
    commit(C.REMINDERS_RULES_REVISIONS_CURRENT);
  },

  /**
   * @param commit
   * @param dispatch
   * @param revision
   */
  setRevision({ commit, dispatch }, revision) {
    commit(C.REMINDERS_RULES_REVISIONS_CURRENT, revision);
    router.replace({
      ...router.currentRoute,
      query: {
        ...router.currentRoute.query,
        revision,
      },
    });
    dispatch('getRules');
  },

  /**
   * @param commit
   * @param state
   */
  getRevisions({ commit, state }) {
    commit(C.REMINDERS_RULES_REVISIONS_PENDING);
    ReminderService.getRevisions(state.rules.domain.id).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_RULES_REVISIONS_NO_CONTENT);
      } else {
        commit(C.REMINDERS_RULES_REVISIONS_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_RULES_REVISIONS_FAILURE);
    });
  },

  /**
   * @param commit
   */
  getPreviewData({ commit }) {
    commit(C.REMINDERS_LISTS_PREVIEW_PENDING);
    ReminderService.getPreviewData().then((res) => {
      commit(C.REMINDERS_LISTS_PREVIEW_SUCCESS, res.data);
    }).catch(() => {
      commit(C.REMINDERS_LISTS_PREVIEW_FAILURE);
    });
  },

  /**
   * @param commit
   */
  getOverview({ commit }) {
    commit(C.REMINDERS_LISTS_OVERVIEW_PENDING);
    ReminderService.getOverview().then((res) => {
      commit(C.REMINDERS_LISTS_OVERVIEW_SUCCESS, res.data);
    }).catch(() => {
      commit(C.REMINDERS_LISTS_OVERVIEW_FAILURE);
    });
  },

  /**
   * @param commit
   * @param list
   */
  getList({ commit }, list) {
    commit(C.REMINDERS_LISTS_LIST_PENDING);
    ReminderService.getList(list).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_LISTS_LIST_SUCCESS, []);
      } else {
        commit(C.REMINDERS_LISTS_LIST_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_LISTS_LIST_FAILURE);
    });
  },

  /**
   * @param commit
   * @param booking
   */
  getPaymentMessages({ commit }, booking) {
    commit(C.REMINDERS_LISTS_MESSAGES_PENDING, booking);
    ReminderService.getPaymentMessages(booking.bookingId).then((res) => {
      if (res.status === 204) {
        commit(C.REMINDERS_LISTS_MESSAGES_SUCCESS, []);
      } else {
        commit(C.REMINDERS_LISTS_MESSAGES_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.REMINDERS_LISTS_MESSAGES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param filters
   */
  updateListFilters({ commit }, filters) {
    commit(C.REMINDERS_LISTS_FILTERS_UPDATE, filters);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/reminders/actions.js